import type { Apparence } from './apparence';
import { api, EchecApi } from './api/client';
import { poserEnAttente } from './api/fileHorsLigne';
import type { ChampsALaNaissance } from './api/types';
import { PAS_DE_RESEAU } from './vocabulaire';

/**
 * Créer une bulle, ou la mettre en file quand le réseau manque (docs/adr/0001, spec `telephone`,
 * « Sans réseau », chantier nettoyage-gestes, ticket 01). **Seule la création survit sans
 * réseau**, et seulement deux d'entre elles : le + du téléphone (Tâche, Idée, un projet) et le
 * champ du bas d'une feuille, à l'ordinateur. Elles attendent dans l'appareil, avec le compte qui
 * les a faites, et partent au retour du réseau. Toute autre création échoue et le dit.
 */

/**
 * L'option de toutes les créations. Sans elle, react-query met le geste en pause tant que le
 * navigateur se croit hors ligne : rien ne part, ni au serveur ni dans la file, et l'écrit se
 * perd si l'app se ferme avant le réseau.
 */
export const OPTIONS_DE_CREATION = { networkMode: 'always' } as const;

export interface DemandeDeCreation {
  titre: string;
  parentId: string | null;
  /** Ce avec quoi elle naît, dans la même demande. La file n'en garde que l'Idée (En réflexion). */
  champs?: ChampsALaNaissance;
  /** Cette création a-t-elle droit à la file quand le réseau manque ? */
  file: boolean;
}

/** Créée (son identifiant), ou en file, qui partira au retour du réseau. */
export type Creation = { id: string } | { enFile: true };

export const estHorsLigne = (erreur: unknown): boolean => erreur instanceof EchecApi && erreur.code === 'hors_ligne';

/**
 * Le geste : une demande au serveur ; si le réseau manque et que la création y a droit, la file,
 * au nom de `utilisateur` (qui est connecté, lu seulement à ce moment-là). Personne de connecté,
 * ou la file refusée par le navigateur : l'échec remonte, comme un refus du serveur.
 */
export async function creerOuMettreEnFile(demande: DemandeDeCreation, utilisateur: () => string | null): Promise<Creation> {
  const { titre, parentId, champs, file } = demande;
  try {
    return { id: (await api.creer(titre, parentId, champs ?? {})).bulle.id };
  } catch (erreur) {
    const qui = file && estHorsLigne(erreur) ? utilisateur() : null;
    if (qui && (await poserEnAttente(titre, parentId, qui, champs?.etat === 'sans_objet'))) return { enFile: true };
    throw erreur;
  }
}

/**
 * Ce avec quoi naît une bulle créée par les champs de l'ordinateur : un projet (sans mère) reçoit
 * ce que Paramètres règle sous « Par défaut, un projet neuf », la forme de ses bulles (chantier
 * habit-du-projet, ticket 03), le fond de sa carte (chantier projet-neuf), le type et le papier de ses fiches (chantier types-de-fiche,
 * modification 06), l'angle sur lequel il s'ouvre (chantier angle-a-l-ouverture). Une bulle dans une autre n'a ni habit ni fiches par défaut ; au téléphone, le
 * projet naît en Galet, ses fiches Classique sur feuille blanche (ni l'habit ni les types de fiche
 * ne s'y voient ni ne s'y règlent).
 */
export function champsDUnProjetNeuf(
  parentId: string | null,
  parDefaut: Pick<Apparence, 'forme' | 'fondDeProjet' | 'typeDeFiche' | 'papierDeFiche' | 'angleALOuverture'>,
  auPouce: boolean
): ChampsALaNaissance {
  if (parentId !== null || auPouce) return {};
  return {
    forme: parDefaut.forme,
    fond: parDefaut.fondDeProjet,
    typeParDefaut: parDefaut.typeDeFiche,
    papierParDefaut: parDefaut.papierDeFiche,
    angle: parDefaut.angleALOuverture,
  };
}

/** Ce qu'une création sans droit à la file dit quand le réseau manque ; `null` pour tout autre cas. */
export function refusDeCreation(erreur: unknown): string | null {
  return estHorsLigne(erreur) ? PAS_DE_RESEAU : null;
}
